import React, { useEffect, useRef, useState } from "react";
import { Alert, Spinner } from "reactstrap";
import { useTranslation } from "react-i18next";
import {
  isGoogleLoginEnabled,
  mountGoogleSignInButton,
  promptGoogleOneTap,
} from "../../helpers/google_auth_helper";

type Props = {
  /** Chamado com o id_token do GIS. */
  onCredential: (idToken: string) => void;
  /** Desabilita clique enquanto a API processa. */
  busy?: boolean;
  /** Largura do botão Google (px). */
  width?: number;
  className?: string;
  /** One Tap flutuante (login/cadastro). */
  enableOneTap?: boolean;
};

/**
 * Botão oficial “Continuar com Google” (GIS).
 * Só renderiza se REACT_APP_GOOGLE_CLIENT_ID estiver setado.
 */
const GoogleSignInButton = ({
  onCredential,
  busy = false,
  width = 320,
  className = "",
  enableOneTap = false,
}: Props) => {
  const { t, i18n } = useTranslation();
  const hostRef = useRef<HTMLDivElement | null>(null);
  // Ref evita remount do GIS a cada render do pai (callback inline).
  const onCredentialRef = useRef(onCredential);
  onCredentialRef.current = onCredential;
  const [loadError, setLoadError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const enabled = isGoogleLoginEnabled();

  useEffect(() => {
    if (!enabled || !hostRef.current) {
      return;
    }
    let cancelled = false;
    let cleanup: (() => void) | undefined;
    let cleanupOneTap: (() => void) | undefined;

    setLoadError(null);
    setReady(false);

    mountGoogleSignInButton(
      hostRef.current,
      (token) => {
        if (!cancelled) {
          onCredentialRef.current(token);
        }
      },
      { locale: i18n.language || "pt-BR", width }
    )
      .then(async (fn) => {
        if (cancelled) {
          fn();
          return;
        }
        cleanup = fn;
        setReady(true);
        if (enableOneTap) {
          cleanupOneTap = await promptGoogleOneTap((token) => {
            if (!cancelled) {
              onCredentialRef.current(token);
            }
          });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLoadError(t("auth.google.loadError"));
        }
      });

    return () => {
      cancelled = true;
      cleanup?.();
      cleanupOneTap?.();
    };
  }, [enabled, enableOneTap, i18n.language, t, width]);

  if (!enabled) {
    return null;
  }

  return (
    <div className={`google-signin-wrap ${className}`.trim()}>
      <div className="d-flex align-items-center my-3">
        <div className="flex-grow-1 border-top" />
        <span className="px-2 text-muted small">{t("auth.google.or")}</span>
        <div className="flex-grow-1 border-top" />
      </div>
      {loadError ? (
        <Alert color="warning" className="py-2" role="status">
          {loadError}
        </Alert>
      ) : null}
      <div
        className="d-flex justify-content-center position-relative"
        style={{
          minHeight: 44,
          opacity: busy ? 0.5 : 1,
          pointerEvents: busy ? "none" : "auto",
        }}
      >
        {!ready && !loadError ? (
          <Spinner size="sm" className="position-absolute top-50 start-50" />
        ) : null}
        <div ref={hostRef} aria-label={t("auth.google.continue")} />
      </div>
    </div>
  );
};

export default GoogleSignInButton;
